import { Flex, TextProps, Tooltip } from "@chakra-ui/react";
import moment from "moment";
import CDS from "design-system";
import { Task, TaskSummary } from "../models";

function getRelativeTimeAgo(dueOn: Date): string {
  const now = moment();
  const targetDate = moment(dueOn);
  const daysDiff = targetDate.diff(now.startOf("day"), "days");

  if (daysDiff === -1) return "Yesterday";
  if (daysDiff === 0) return "Today";
  if (daysDiff === 1) return "Tomorrow";

  // Past dates
  if (daysDiff < -1) {
    const absDays = Math.abs(daysDiff);
    if (absDays < 7) return `${absDays} days ago`;
    const weeks = Math.floor(absDays / 7);
    if (weeks < 5) return `${weeks} ${weeks === 1 ? "week" : "weeks"} ago`;
    const months = Math.floor(absDays / 30);
    return `${months} ${months === 1 ? "month" : "months"} ago`;
  }

  // Future dates within next week
  if (daysDiff >= 2 && daysDiff <= 7) {
    return targetDate.format("[Next] dddd");
  }

  // Future dates
  if (daysDiff > 7) {
    const weeks = Math.floor(daysDiff / 7);
    if (weeks < 5) return `In ${weeks} ${weeks === 1 ? "week" : "weeks"}`;
    const months = Math.floor(daysDiff / 30);
    return `In ${months} ${months === 1 ? "month" : "months"}`;
  }

  return "";
}

interface DueDateTextWithColorProps extends Omit<TextProps, "children"> {
  task: Task | TaskSummary;
  showRelative?: boolean;
}

export default function DueDateTextWithColor({ task, showRelative = false, ...textProps }: DueDateTextWithColorProps) {
  const { dueOn, isOverdue, isUrgent } = task;

  // Don't show due date for completed, skipped, or upcoming tasks
  const shouldHide = ["completed", "skipped", "upcoming"].includes(task.status);

  if (!dueOn || shouldHide) {
    return null;
  }

  const getDueDateColor = () => {
    if (isOverdue) return "statusRed.text";
    if (isUrgent) return "cdsOrange.500";
    return "gray.600";
  };

  const getTooltipLabel = () => {
    if (isOverdue) return "Overdue";
    if (isUrgent) return "Urgent";
    return null;
  };

  const dateStr = moment(dueOn).format("MMM D");
  const relativeStr = getRelativeTimeAgo(dueOn);

  // For Yesterday, Today, Tomorrow - just show the word
  // Otherwise show "Feb 18 (2 days ago)" format
  const simpleRelatives = ["Yesterday", "Today", "Tomorrow"];
  let displayText = dateStr;
  if (showRelative && relativeStr) {
    if (simpleRelatives.includes(relativeStr)) {
      displayText = relativeStr;
    } else {
      displayText = `${dateStr} (${relativeStr})`;
    }
  }

  const tooltipLabel = getTooltipLabel();

  return (
    <Tooltip label={tooltipLabel} isDisabled={!tooltipLabel} placement="top">
      <Flex align="center" h="100%">
        <CDS.Text12
          fontWeight="medium"
          color={getDueDateColor()}
          {...textProps}
        >
          {displayText}
        </CDS.Text12>
      </Flex>
    </Tooltip>
  );
}
